/**
 * Headings and labels: the type scale in globals.css as components. Use these; if a screen needs a
 * heading none of them draws, the answer is a new role in the scale, not a class at the call site.
 *
 *   <PageTitle>     the page's own name — a job's name, "Settings"          title   20
 *   <SectionTitle>  a panel or section heading                              heading 15
 *   <Eyebrow>       the uppercase label above a group of fields or a table  eyebrow 11
 *   <Muted>         supporting text under a title                           small   13
 */
import type { ComponentProps, ElementType } from "react";

import { cn } from "@/lib/utils";

type Props<T extends ElementType> = { as?: T } & Omit<ComponentProps<T>, "as">;

export function PageTitle<T extends ElementType = "h1">({ as, className, ...props }: Props<T>) {
  const Tag = (as ?? "h1") as ElementType;
  return (
    <Tag
      data-slot="page-title"
      className={cn("font-heading text-title font-semibold tracking-tight text-foreground", className)}
      {...props}
    />
  );
}

export function SectionTitle<T extends ElementType = "h2">({ as, className, ...props }: Props<T>) {
  const Tag = (as ?? "h2") as ElementType;
  return (
    <Tag
      data-slot="section-title"
      className={cn("font-heading text-heading font-semibold text-foreground", className)}
      {...props}
    />
  );
}

export function Eyebrow<T extends ElementType = "div">({ as, className, ...props }: Props<T>) {
  const Tag = (as ?? "div") as ElementType;
  return <Tag data-slot="eyebrow" className={cn("eyebrow", className)} {...props} />;
}

export function Muted<T extends ElementType = "p">({ as, className, ...props }: Props<T>) {
  const Tag = (as ?? "p") as ElementType;
  return <Tag data-slot="muted" className={cn("text-small text-muted-foreground", className)} {...props} />;
}
